import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { PDFViewer } from '../components/PDFViewer';
import { Download, Eye, FileText, BookOpen } from 'lucide-react';

const KnowledgeCentrePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPDFViewer, setShowPDFViewer] = useState(false);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Knowledge centre resources
  const resources = [
    {
      id: 5,
      title: "Cybersecurity: An emerging investment theme in the AI age",
      description: "Discover a few Indian, publicly listed Cybersecurity companies building and deploying solutions to combat modern day risks in cyberspace.",
      type: "PDF Report",
      pages: 8,
      coverImage: "/assets/knowledge-centre/cybersecurity-ai-page-1.jpg",
      pdfUrl: "/assets/knowledge-centre/cybersecurity-ai.pdf",
      allPages: Array.from({ length: 8 }, (_, i) => 
        `/assets/knowledge-centre/cybersecurity-ai-page-${i + 1}.jpg`
      ),
      category: "Sector Analysis",
      date: "October 2026"
    },
    {
      id: 4,
      title: "HDFC Bank vs ICICI Bank: A tale of narrowing market cap difference",
      description: "The biggest private, systemically important banks of India are getting contradictory treatments in the stock market. How do they stack up against one another?",
      type: "PDF Report",
      pages: 16,
      coverImage: "/assets/knowledge-centre/hdfc-icici-page-1.jpg",
      pdfUrl: "/assets/pdfs/hdfc-icici-banks.pdf",
      allPages: Array.from({ length: 16 }, (_, i) => 
        `/assets/knowledge-centre/hdfc-icici-page-${i + 1}.jpg`
      ),
      category: "Banking Analysis",
      date: "August 2026"
    },
    {
      id: 3,
      title: "Taj vs The Oberoi: How the very elite in Indian hospitality function?",
      description: "While anyone would want to experience both in their lifetime, let's unwrap which one makes the cut for a compelling long-term investment.",
      type: "PDF Report",
      pages: 15,
      coverImage: "/assets/knowledge-centre/taj-oberoi-page-1.jpg",
      pdfUrl: "/assets/pdfs/taj-vs-oberoi.pdf",
      allPages: Array.from({ length: 15 }, (_, i) => 
        `/assets/knowledge-centre/taj-oberoi-page-${i + 1}.jpg`
      ),
      category: "Company Analysis",
      date: "July 2026"
    },
    {
      id: 2,
      title: "Coal & India's Energy Security - Where the two converge?",
      description: "Know why Coal can be India's strategic (and emission-free) asset for Energy Security.",
      type: "PDF Report",
      pages: 15,
      coverImage: "/assets/knowledge-centre/coal-energy-page-1.jpg",
      pdfUrl: "/assets/pdfs/coal-india-energy-security.pdf",
      allPages: Array.from({ length: 15 }, (_, i) => 
        `/assets/knowledge-centre/coal-energy-page-${i + 1}.jpg`
      ),
      category: "Energy & Infrastructure",
      date: "June 2026"
    },
    {
      id: 1,
      title: "Jio vs Airtel: Decoding the two Telecom behemoths",
      description: "Dive into the businesses of Reliance Jio and Bharti Airtel, the definitive faces of Indian telecom.",
      type: "PDF Report",
      pages: 11,
      coverImage: "/assets/knowledge-centre/jio-airtel-page-1.jpg",
      pdfUrl: "/assets/pdfs/jio-vs-airtel.pdf",
      allPages: Array.from({ length: 11 }, (_, i) => 
        `/assets/knowledge-centre/jio-airtel-page-${i + 1}.jpg`
      ),
      category: "Sector Analysis",
      date: "April 2026"
    }
  ];

  const [currentResource, setCurrentResource] = useState(null);

  const handleViewPDF = (resource) => {
    setCurrentResource(resource);
    setShowPDFViewer(true);
  };

  const handleDownload = (pdfUrl, title) => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = title.replace(/[^a-z0-9]/gi, '-').toLowerCase() + '.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Knowledge Centre | Savi Investing"
        description="A repository of our research and analysis - businesses, industries, and investing methodologies."
      />
      <Header />
      
      <main className="flex-grow pt-24">
        {/* Page Header */}
        <section className="bg-[#29367A] text-white py-24">
          <div className="w-full max-w-7xl mx-auto px-8">
            <div 
              className={`transition-all duration-1000 transform ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="flex items-center justify-center mb-6">
                <BookOpen className="w-12 h-12 mr-4" />
              </div>
              <h1 className="text-5xl font-bold text-center mb-6">
                Knowledge Centre
              </h1>
              <p className="text-xl text-center text-white/90 max-w-3xl mx-auto leading-relaxed">
                Empowering investors with insights, research, and knowledge on value investing and the Indian stock market. 
                Explore our curated collection of reports, articles, and resources.
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-gray-50">
          <div className="w-full max-w-7xl mx-auto px-8">
            {/* Section Intro */}
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Latest Resources
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Deep dives into businesses, sectors, and investing methodologies that matter.
              </p>
            </div>

            {/* Resources Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {resources.map((resource) => (
                <div 
                  key={resource.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  {/* Cover Image */}
                  <div className="relative h-64 overflow-hidden bg-gray-200 group cursor-pointer"
                    onClick={() => handleViewPDF(resource)}
                  >
                    <img
                      src={resource.coverImage}
                      alt={resource.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-4">
                        <Eye className="w-8 h-8 text-[#29367A]" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                      {resource.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {resource.description}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4 pb-4 border-b">
                      <span>{resource.pages} pages</span>
                      <span>{resource.date}</span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleViewPDF(resource)}
                        className="flex-1 bg-[#29367A] hover:bg-[#1f2859] text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Online
                      </button>
                      <button
                        onClick={() => handleDownload(resource.pdfUrl, resource.title)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State / Coming Soon */}
            <div className="mt-12 text-center bg-white rounded-xl shadow-md p-12">
              <BookOpen className="w-16 h-16 text-[#29367A] mx-auto mb-4 opacity-50" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">More Content Coming Soon</h3>
              <p className="text-gray-600 max-w-xl mx-auto">
                We're constantly adding new research reports, articles, and insights. 
                Stay tuned for more valuable content on value investing and the Indian markets.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* PDF Viewer Modal */}
      {showPDFViewer && currentResource && (
        <PDFViewer
          pages={currentResource.allPages}
          title={currentResource.title}
          pdfUrl={currentResource.pdfUrl}
          onClose={() => setShowPDFViewer(false)}
        />
      )}
    </div>
  );
};

export default KnowledgeCentrePage;
